import { DetailsTrainingItem } from '@/app/models/detailsTraining';
import { FinancialGroup } from '@/app/models/financalGroup';
import { TransferDetailBase } from '@/app/models/transferDetail';
import { useSearchDetailsTrainingQuery, useUnitTrainingMutation } from '@/app/services/detailsTrainingApi';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import formatterMonetaryValue, { formatHandleCPFAndCNPJ } from '@/app/utils/numberUtils';
import LateralDrawer from '@/components/common/LateralDrawer';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { usePeriod } from '@/context/PeriodContext';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import GenericAutoComplete from '../../common/GenericAutoComplete';
import NewEditTransferCost from '../NewEditTransferCost';
import NewEditTrainingDetails from './NewEditTrainingDetails';
import TrainingTable from './TrainingTable';

interface TrainingProps {
  group: FinancialGroup;
}

function Training(props: TrainingProps) {
  const theme = useTheme();

  const { group } = props;
  const { code } = useParams();
  const period = usePeriod();

  const { t } = useTranslation(['training', 'common']);

  const { data: financialsubGroup } = useFinancialSubGroupQuery({ groupCode: group?.id }, { skip: !group });
  const { selectedFinancialSubGroup, setSelectedFinancialSubGroup } = useFinancialSubGroup();
  useEffect(() => {
    if (financialsubGroup && financialsubGroup.length > 0) {
      const found = financialsubGroup.find((item) => item.name === group.name) ?? financialsubGroup[0];
      setSelectedFinancialSubGroup(found);
    }
  }, [financialsubGroup, group, setSelectedFinancialSubGroup]);

  const [originSupplier, setOriginSupplier] = useState<DetailsTrainingItem | null>(null);
  const [originSupplierSearch, setOriginSupplierSearch] = useState('');

  const { data: training } = useSearchDetailsTrainingQuery(
    {
      period: period.selectedPeriod?.date as string,
      project: code || '',
      subGroup: selectedFinancialSubGroup?.id as number,
      searchTerm: originSupplierSearch || '',
    },
    {
      skip: !period.selectedPeriod?.date || !code || !selectedFinancialSubGroup,
    },
  );

  const [selectTraining, setSelectTraining] = useState<number[]>([]);
  const [drawerEdit, setDrawerEdit] = useState(false);
  const [drawerTransfer, setDrawerTransfer] = useState(false);

  const [selectTrainingItem, setSelectedTrainingItem] = useState<DetailsTrainingItem>();

  const [unit] = useUnitTrainingMutation();

  const handleUnite = async () => {
    await toast.promise(
      unit({
        ids: selectTraining,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setSelectTraining([]);
  };
  const [selectedItem, setSelectedItem] = useState<TransferDetailBase | undefined>();
  const { selectedFinancialGroup } = useFinancialGroup();
  useEffect(() => {
    if (!selectTrainingItem || !selectedFinancialSubGroup || !period.selectedPeriod?.date) return;

    setSelectedItem({
      id: selectTrainingItem?.id,
      originFinancialGroupCode: selectedFinancialGroup?.code as string,
      financialSubGroupId: selectedFinancialSubGroup.id,
      period: period.selectedPeriod.date,
      complement: selectTrainingItem.complement,
      newDetail: {
        description: selectTrainingItem.supplier,
        value: selectTrainingItem.value,
      },
    });
  }, [selectedFinancialSubGroup, selectedFinancialGroup?.code, selectTrainingItem, period.selectedPeriod]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="fieldHeader">{t('common:search')}</Typography>
            <GenericAutoComplete
              value={originSupplier}
              onChange={(value) => {
                setOriginSupplier(value);
              }}
              equalityTest={(option, value) => option?.supplier === value?.supplier}
              getOptionLabel={(option) =>
                option ? `${option.supplier ?? ''} - ${formatHandleCPFAndCNPJ(option.supplierCode) ?? ''}` : ''
              }
              onSearch={(value) => {
                setOriginSupplierSearch(value);
              }}
              options={[]}
              placeholder={t('common:type.to.search') as string}
              sx={{ width: theme.spacing(80) }}
            />
          </Box>
          <Box sx={{ minWidth: theme.spacing(31) }}>
            <Typography variant="fieldHeader">
              {t('common:total.by.group')}
              <Typography>{formatterMonetaryValue(training?.totalValue || null)}</Typography>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <Stack direction="row" alignItems="center">
            <Button
              variant="primary"
              sx={{ width: theme.spacing(20) }}
              onClick={() => handleUnite()}
              disabled={selectTraining.length === 0}
            >
              {t('common:unite')}
            </Button>
          </Stack>
        </Box>
      </Box>

      <TrainingTable
        selectTraining={selectTraining}
        setSelectTraining={setSelectTraining}
        setDrawerEdit={setDrawerEdit}
        setSelectedTrainingItem={setSelectedTrainingItem}
        selectedTrainingItem={selectTrainingItem as DetailsTrainingItem}
        setDrawerTransfer={setDrawerTransfer}
        equipmentData={training}
      />
      <LateralDrawer open={drawerEdit} setOpen={setDrawerEdit} position="right" size="bigger">
        <NewEditTrainingDetails selectItem={selectTrainingItem as DetailsTrainingItem} setDrawerOpen={setDrawerEdit} />
      </LateralDrawer>
      <LateralDrawer open={drawerTransfer} setOpen={setDrawerTransfer} position="right" size="bigger">
        <NewEditTransferCost selectedItem={selectedItem as TransferDetailBase} setDrawerOpen={setDrawerTransfer} />
      </LateralDrawer>
    </Box>
  );
}

export default Training;
